import pipe from '../utils/pipe.js';
import API from '../core/API.js';
import State from '../core/State.js';
import Intro from './Intro.js';
import Modal from './Modal.js';
import Song from './Song.js';
import Stripe from './Stripe.js';

const createPageStruct = function (pageOpts) {
    return {
        state: createPageState(),
        dom: {
            el: pageOpts.el,
            intro: pageOpts.el.querySelector('.intro'),
            modal: pageOpts.el.querySelector('.modal'),
            paginationTrigger: pageOpts.el.querySelector('.pagination-trigger'),
            paginationAutoTrigger: pageOpts.el.querySelector('.pagination-trigger-auto'),
            timeline: pageOpts.el.querySelector('.timeline-song-list')
        },
        modal: null,
        pagination: {
            page: 1,
            loading: null,
            allLoaded: null
        }
    };
};

const createPageState = function () {
    return State({});
};

const initIntro = function (pageStruct) {
    const {
        dom
    } = pageStruct;

    pageStruct.intro = null;

    if (dom.intro) {
        pageStruct.intro = Intro({
            el: dom.intro
        });
    }

    return pageStruct;
};

const initModal = function (pageStruct) {
    const {
        dom
    } = pageStruct;

    pageStruct.modal = Modal({
        el: dom.modal
    });

    return pageStruct;
};

const modalContentPath = function (linkHref) {
    const linkPaths = [
        '/about',
        '/login',
        '/reset-password',
        '/join-direct',
        '/join-fanclub'
    ];

    for (let i = 0; i < linkPaths.length; i++) {
        let linkPath = linkPaths[i];
        if (linkHref.indexOf(location.origin + linkPath) === 0) {
            return true;
        }
    }

    return false;
};

const closestAnchor = function (element) {
    let parentNode = element;

    while (parentNode && parentNode.tagName !== 'A') {
        parentNode = parentNode.parentNode;
    };

    return parentNode;
};

const initAutoPagination = function (pageStruct) {
    const {
        dom,
        pagination
    } = pageStruct;

    loadNextPage(pageStruct);
    pagination.autoPaginate = true;
    dom.paginationAutoTrigger.classList.add('state-hidden');
    setTimeout(function () {
        dom.paginationAutoTrigger.parentNode.remove();
    }, 600);
};

const initEventDelegation = function (pageStruct) {
    const {
        dom
    } = pageStruct;

    dom.paginationAutoTrigger.addEventListener('click', (evnt) => {
        initAutoPagination(pageStruct);
        evnt.preventDefault();
    });

    window.addEventListener('scroll', (evnt) => {
        handlePagination(evnt, pageStruct);
    });

    dom.el.addEventListener('click', (evnt) => {
        const targetAnchor = closestAnchor(evnt.target);

        if (
            targetAnchor && targetAnchor.matches('a[href]') &&
            modalContentPath(targetAnchor.href)
        ) {
            evnt.preventDefault();
            window.history.pushState(null, null, targetAnchor.href);
            loadModalContent(pageStruct);
        }
    });

    dom.el.addEventListener('submit', (evnt) => {
        if (
            evnt.target && evnt.target.matches('form')
        ) {
            handleFormSubmit(evnt, pageStruct);
        }
    });

    return pageStruct;
};

const handlePagination = function (evnt, pageStruct) {
    const {
        dom,
        pagination
    } = pageStruct;

    if (pagination.allLoaded) {
        return;
    }

    const paginationTriggerBounds = dom.paginationTrigger.getBoundingClientRect();

    if (
        pagination.autoPaginate &&
        paginationTriggerBounds.bottom < window.innerHeight &&
        !pagination.loading
    ) {
        loadNextPage(pageStruct);
    }
};

const loadNextPage = function (pageStruct) {
    const {
        dom,
        pagination
    } = pageStruct;

    let targetPage = pagination.page + 1;
    pagination.loading = true;
    API.GET('/ajax/timeline/?page=' + targetPage).then((response) => {
        const pageFrag = document.createElement('ul');
        pageFrag.innerHTML = response;

        if (pageFrag.children.length) {
            while (pageFrag.children.length) {
                let newTimelineItem = dom.timeline.appendChild(pageFrag.firstChild);

                if (newTimelineItem.querySelector) {
                    let songEl = newTimelineItem.querySelector('.song');
                    if (songEl) {
                        initSong(pageStruct, newTimelineItem.querySelector('.song'));
                    }
                }
            }
        } else {
            pagination.allLoaded = true;
            dom.paginationTrigger.classList.add('state-hidden');
        }

        pagination.page = targetPage;
        pagination.loading = false;
    });
};

const handleFormSubmit = function (evnt, pageStruct) {
    const {
        modal
    } = pageStruct;
    const { target: formEl } = evnt;
    const formData = new FormData(formEl);

    evnt.preventDefault();

    const formLoadingSpinner = document.createElement('div');
    formLoadingSpinner.classList.add('loading-spinner');
    formEl.appendChild(formLoadingSpinner);
    formEl.classList.add('state-loading');

    API.POST(formEl.action, formData).then((content) => {
        if (!content) { return; }

        if (typeof content === 'string') {
            modal.launch(content);
        } else if (content.getStripeToken) {
            Stripe({
                formEl,
                formData,
                modal
            });
        }
    });
};

const listenToWindowPopstateEvent = function (pageStruct) {
    window.onpopstate = () => {
        loadModalContent(pageStruct);
    };

    return pageStruct;
};

const loadModalContent = function (pageStruct, url = document.location.toString()) {
    const {
        modal
    } = pageStruct;

    if (modalContentPath(url)) {
        modal.launch({ loading: true });

        API.GET(url).then((content) => {
            modal.launch(content);
        });
    } else {
        modal.close();
    }
};

const pageStart = function (pageStruct) {
    pipe(pageStruct, [
        launchIntro,
        launchModal,
        initSongs
    ]);
};

const launchIntro = function (pageStruct) {
    const {
        intro
    } = pageStruct;

    if (intro) {
        intro.launch();
    }

    return pageStruct;
};

const launchModal = function (pageStruct) {
    const {
        dom,
        modal
    } = pageStruct;

    if (/true/.test(dom.modal.dataset.hasContent)) {
        modal.launch();
    }

    return pageStruct;
};

const initSongs = function (pageStruct) {
    const {
        dom
    } = pageStruct;
    const songEls = dom.el.querySelectorAll('.song');

    songEls.forEach(songEl => {
        initSong(pageStruct, songEl);
    });

    return pageStruct;
};

const initSong = function (pageStruct, songEl) {
    const {
        state
    } = pageStruct;
    const songState = state.newSubState({
        code: null,
        key: null
    });
    Song({ el: songEl, state: songState });
};

const createPageAPI = function (pageStruct) {
    return {
        start () {
            pageStart(pageStruct);
        }
    };
};

const Page = function (pageOpts) {
    let pageStruct = createPageStruct(pageOpts);
    pageStruct = pipe(pageStruct, [
        initIntro,
        initModal,
        initEventDelegation,
        listenToWindowPopstateEvent
    ]);
    return createPageAPI(pageStruct);
};

export default Page;



// WEBPACK FOOTER //
// ./assets/js/components/Page.js